import { validateEnv } from '../project/validateEnv';
import { ClientDeploymentDocument, clientDeploymentSchema } from './ClientDeploymentDocument';
import { z, ZodError } from 'zod';

export const validateDeployment = (
  data: ClientDeploymentDocument,
  options: { [key: string]: any } = {}
): ZodError<ClientDeploymentDocument> => {
  const validData = {
    ...data,
    componentsWithValues: data.componentsWithValues.map((component) => ({
      ...component,
      // Skip empty ENVs
      environmentVariables: component.environmentVariables.filter((env) => env.identifier.length > 0),
    })),
  };

  // Do zod validation
  const ret = clientDeploymentSchema.safeParse(validData);
  const error = ret.success ? new z.ZodError<ClientDeploymentDocument>([]) : ret.error;

  const errorByLike = (like: string) => Object.keys(error.flatten().fieldErrors).some((key) => key.includes(like)) || null;

  // FIXME: Re-implement!
  // validateEnv(error, ['configuration.environmentVariables'], data.configuration?.environmentVariables || []);

  for (let componentIndex = 0; componentIndex < data.componentsWithValues.length || 0; componentIndex++) {
    const component = data.componentsWithValues[componentIndex];

    for (let hostIndex = 0; hostIndex < component.hosts.length || 0; hostIndex++) {
      const host = component.hosts[hostIndex];
      const path = [`componentsWithValues.${componentIndex}.hosts.${hostIndex}`];

      if (host.customDomain) {
        if (host.customDomain.length < 3) {
          error.addIssue({ code: z.ZodIssueCode.custom, path, message: 'Please enter a valid domain' });
        }
      }

      if (host.tweakedDomain !== null) {
        const tweakedDomainRoot = `.${(host.defaultDomain || '').split('.').slice(1).join('.')}`;
        const tweakedDomainSlug = host.tweakedDomain.replace(tweakedDomainRoot, '');

        const res = z
          .string()
          .min(1, 'Please enter a domain')
          .max(16, 'Domain is too long')
          .regex(/^[a-zA-Z0-9_-]*$/g)
          .safeParse(tweakedDomainSlug);
        if (!res.success && res.error) {
          error.addIssues(res.error.issues.map((issue) => ({ ...issue, path })));
        }
      }
    }
  }

  // Create human readable summary of validation situation and add to issues
  let summary: string | null = '';
  const { fieldErrors, formErrors } = error.flatten();
  if (fieldErrors['name']) summary = 'Please check deployment name';
  if (errorByLike('.hosts.')) summary = 'Please check deployment hosts';
  if (errorByLike('.environmentVariables.')) summary = 'Please check environment variables';
  if (error.issues.length && !summary) summary = 'Unexpected error';
  if (error.issues.length) {
    error.addIssue({ code: z.ZodIssueCode.custom, path: ['summary'], message: summary });
  }

  return error;
};
